import { validateItem } from './validateItem'
import {
  OrderConfigItem,
  OrderConfigItemVariant,
  RemoteConfig,
  Step,
} from '@ca/common/types'
import { Refraction } from '@ca/common'
import validateCondition from './validateCondition'
import { VariableValues } from './OrderVariables'

export type ResolvedVariant = OrderConfigItem & {
  isRecommended: boolean
  active: OrderConfigItemVariant
}
export type ResolvedVariants = ResolvedVariant[]

const resolveRecommended = ({
  hasRecommended,
  item,
  values,
  config,
  refraction,
}: {
  hasRecommended: boolean
  item: OrderConfigItemVariant
  values: VariableValues
  config: RemoteConfig
  refraction: Refraction | undefined
}) => {
  if (hasRecommended) {
    return false
  }
  if (!item.recommended) {
    return false
  }
  const valid = validateCondition(
    config.conditions[item.recommended],
    {
      ...values,
      ref: refraction,
    },
    config.conditions
  )
  return valid.result
}

const getActiveVariant = ({
  item,
  values,
  config,
  refraction,
}: {
  item: OrderConfigItem
  values: VariableValues
  config: RemoteConfig
  refraction: Refraction | undefined
}) => {
  if (!item.variants || item.variants.length === 0) {
    return item.base
  }
  const active = item.variants.find((variant) =>
    validateItem(variant, values, config.conditions, refraction).result
  )

  return active
}

/**
 * Resolves the variants for a step and sets the active variant and whether it is recommended
 * Currently, the first variant that matches recommended condition is set as recommended
 *
 */
export const resolveVariants = ({
  config,
  refraction,
  step,
  values,
}: {
  config: RemoteConfig
  refraction: Refraction | undefined
  step: Step
  values: VariableValues
}): ResolvedVariants => {
  const stepItems = step.items.map((item) => config.items[item])
  const items = stepItems.filter((item) =>
    validateItem(item.base, values, config.conditions, refraction).result
  )

  if (!items) {
    return []
  }

  let hasRecommended = false

  return items
    .map((item) => {
      const active = getActiveVariant({
        item,
        values,
        config,
        refraction,
      })
      const isRecommended = resolveRecommended({
        hasRecommended,
        item: item.base,
        values,
        config,
        refraction,
      })
      if (isRecommended) {
        hasRecommended = true
      }

      if (item.requireVariant && !active) {
        return null
      }

      return {
        ...item,
        isRecommended,
        active: active || item.base,
      } satisfies ResolvedVariant
    })
    .filter((v) => !!v)
}
